import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import {
  Survey,
  SurveyInteraction,
  CreateSurveyResponseInput,
  CreateSurveyResponseResult,
  UpdateSurveyResponseInput,
  UpdateSurveyResponseResult,
  DismissSurveyInput,
  DismissSurveyResult,
  ViewSurveyInput,
  ViewSurveyResult
} from 'src/graphql/generated/schema';

@Injectable({ providedIn: 'root' })
export class GraphQLSurveyService {
  private readonly graphqlEndpoint = environment.graphQlURI;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHeaders(): HttpHeaders {
    // Backend verifier expects access token, not id token
    const token = this.authService.getAccessToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  private async executeQuery<T>(query: string, variables: Record<string, any> = {}): Promise<T> {
    const response = await firstValueFrom(
      this.http.post<{ data: T; errors?: any[] }>(
        this.graphqlEndpoint,
        { query, variables },
        { headers: this.getHeaders() }
      ).pipe(
        timeout(15000)
      )
    );

    if (response.errors && response.errors.length > 0) {
      console.error('GraphQL errors:', response.errors);
      throw new Error(response.errors[0].message);
    }

    return response.data;
  }

  /**
   * Fetch active surveys for the current user
   * This includes targeting by building, floor, and user, and excludes dismissed surveys
   */
  async fetchActiveSurveys(): Promise<Survey[]> {
    const query = `
      query GetActiveSurveys {
        getActiveSurveys {
          id
          name
          description
          active
          startDate
          endDate
          targetBuildingIds
          targetFloors
          targetUserIds
          notes
          questions {
            id
            surveyId
            prompt
            type
            options
            required
            order
          }
        }
      }
    `;

    const data = await this.executeQuery<{ getActiveSurveys: Survey[] }>(query);
    return data.getActiveSurveys || [];
  }

  /**
   * Fetch user's responses for a specific survey
   */
  async fetchUserResponsesForSurvey(surveyId: string): Promise<SurveyInteraction[]> {
    const query = `
      query GetSurveyResponses($surveyId: String!) {
        getSurveyResponses(surveyId: $surveyId) {
          id
          surveyId
          questionId
          userId
          type
          answer
          numericValue
          buildingId
          apartmentName
          floor
          metadata
          timestamp
        }
      }
    `;

    const data = await this.executeQuery<{ getSurveyResponses: SurveyInteraction[] }>(query, { surveyId });
    return data.getSurveyResponses || [];
  }

  /**
   * Create a survey response
   */
  async createSurveyResponse(surveyId: string, questionId: string, answer?: string, numericValue?: number): Promise<boolean> {
    const query = `
      mutation CreateSurveyResponse($input: CreateSurveyResponseInput!) {
        createSurveyResponse(input: $input)
      }
    `;

    const input: CreateSurveyResponseInput = { surveyId, questionId };
    if (answer !== undefined) input.answer = answer;
    if (numericValue !== undefined) input.numericValue = numericValue;

    const data = await this.executeQuery<{ createSurveyResponse: CreateSurveyResponseResult }>(query, { input });
    return data.createSurveyResponse === CreateSurveyResponseResult.SurveyResponseCreated;
  }

  /**
   * Update an existing survey response
   */
  async updateSurveyResponse(interactionId: string, answer?: string, numericValue?: number): Promise<boolean> {
    const query = `
      mutation UpdateSurveyResponse($input: UpdateSurveyResponseInput!) {
        updateSurveyResponse(input: $input)
      }
    `;

    const input: UpdateSurveyResponseInput = { interactionId };
    if (answer !== undefined) input.answer = answer;
    if (numericValue !== undefined) input.numericValue = numericValue;

    const data = await this.executeQuery<{ updateSurveyResponse: UpdateSurveyResponseResult }>(query, { input });
    return data.updateSurveyResponse === UpdateSurveyResponseResult.SurveyResponseUpdated;
  }

  /**
   * Dismiss a survey
   */
  async dismissSurvey(surveyId: string): Promise<boolean> {
    const query = `
      mutation DismissSurvey($input: DismissSurveyInput!) {
        dismissSurvey(input: $input)
      }
    `;

    const input: DismissSurveyInput = { surveyId };
    const data = await this.executeQuery<{ dismissSurvey: DismissSurveyResult }>(query, { input });
    return data.dismissSurvey === DismissSurveyResult.SurveyDismissed;
  }

  /**
   * Record a survey view
   */
  async viewSurvey(surveyId: string): Promise<boolean> {
    const query = `
      mutation ViewSurvey($input: ViewSurveyInput!) {
        viewSurvey(input: $input)
      }
    `;

    const input: ViewSurveyInput = { surveyId };
    const data = await this.executeQuery<{ viewSurvey: ViewSurveyResult }>(query, { input });
    return data.viewSurvey === ViewSurveyResult.SurveyViewed;
  }
}
